import { PRODUCT_TYPE_CONFIGURABLE, PRODUCT_TYPE_SIMPLE } from '@bluheadless/ui-logic/src/constants'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useProductContext } from '@bluheadless/ui-logic/src/providers/product'
import { useAvailabilityInStoreContext } from '@bluheadless/ui-logic/src/providers/availability-in-store'
import { Grid } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/system'
import { cx } from '@emotion/css'
import { func, object } from 'prop-types'
import { useRef, forwardRef } from 'react'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import ProductOptions from '../../../molecules/product-options'
import { QtyUI } from '../../../molecules/qty'
import InstallmentsActionProduct from '../installments-action'
import ButtonsAction from './buttons-action'
import RelatedAttributes from './related-attributes'
import SidebarBottom from './sidebar-bottom'
import SidebarTop from './sidebar-top'
import { ButtonAvailabilityInStore, OptionsWrapper, QtyLabel, Root } from './sidebar.styled'
import StickyButtons from './sticky-buttons'
import CustomOptions from '../../../molecules/custom-options'

const Sidebar = forwardRef(function Sidebar({ className, reviewsRef = null, inViewRef = null, ...props }, ref) {
	const [, { openAvailabilityInStore }] = useAvailabilityInStoreContext()
	const { formatMessage } = useIntl()
	const optionsRef = useRef(null)
	const productContextValue = useProductContext()
	const theme = useTheme()
	const smUp = useMediaQuery(theme.breakpoints.up('sm'))
	const [gridActionRef, inView] = useInView({
		threshold: 0.5,
		triggerOnce: false,
	})

	const {
		catalog: {
			products: { optionsColumns, stickyAddToCart },
			swatches: { productPageAttributesProductImage },
		},
		storelocator: { availabilityInStore: { enabled: enabledAvailabilityInStore } } = {},
		wishList: { enabled: wishListEnabled },
	} = useConfig()

	const [
		{
			showQty,
			qty,
			minQty,
			maxQty,
			product: { type: productType },
			selectedProduct,
			inventory: { saleable, inStock },
		},
		{ incrementQty, decrementQty, validateMinimalSelection },
	] = productContextValue

	return (
		<Root className={cx('SidebarProduct-root', className)} ref={ref} type={productType} {...props}>
			<SidebarTop reviewsRef={reviewsRef} />

			<OptionsWrapper columns={optionsColumns} ref={optionsRef}>
				<RelatedAttributes showLabel isProductPage />
				<ProductOptions attributesProductImage={productPageAttributesProductImage} />
			</OptionsWrapper>

			<CustomOptions />

			{saleable && showQty && (inStock || wishListEnabled) && (
				<Grid item mt={3} display="flex" flexDirection="column" xs={6} sm={5}>
					<QtyLabel mb={1} color="grey.main">
						{formatMessage({ id: 'qty' })}
					</QtyLabel>
					<QtyUI decrement={decrementQty} increment={incrementQty} value={qty} min={minQty} max={maxQty} />
				</Grid>
			)}
			<Grid ref={gridActionRef}>
				<ButtonsAction />
				<Grid ref={inViewRef}></Grid>

				{stickyAddToCart[productType] && !smUp && (
					<StickyButtons show={!inView}>
						<ButtonsAction isSticky />
					</StickyButtons>
				)}
			</Grid>

			{saleable && <InstallmentsActionProduct />}

			{enabledAvailabilityInStore &&
				(productType == PRODUCT_TYPE_CONFIGURABLE || productType == PRODUCT_TYPE_SIMPLE) &&
				!selectedProduct?.attributes?.disable_store_availability?.value && (
					<ButtonAvailabilityInStore
						variant="outlined"
						fullWidth
						onClick={async () => {
							if (await validateMinimalSelection()) {
								openAvailabilityInStore(selectedProduct?.sku)
							}
						}}
					>
						{formatMessage({ id: 'availability_in_store' })}
					</ButtonAvailabilityInStore>
				)}
			<SidebarBottom />
		</Root>
	)
})

Sidebar.propTypes = {
	reviewsRef: object,
	stickyRef: func,
}

export default Sidebar
